import { createSlice, isPending, isRejected, PayloadAction } from '@reduxjs/toolkit';
import { showApiErrors } from '@/utils';
import { UsersListState, USERS_LIST_SLICE_NAME, initialState } from './models';
import {
  getAdminDetails,
  getAdminsList,
  getUsersList,
  updateAdminDetails,
  toggleActiveUser,
  createAdmin,
  deleteAdmin,
} from './actionCreators';
import { TGetAdminsQuery, TGetUsersQuery } from '../helpers/types';

export const slice = createSlice({
  name: USERS_LIST_SLICE_NAME,
  initialState,
  reducers: {
    resetUsersListState() {
      return { ...initialState };
    },
    changeQuery: (state: UsersListState, action: PayloadAction<TGetAdminsQuery>) => {
      state.query = action.payload;
    },
    changeUserQuery: (state: UsersListState, action: PayloadAction<TGetUsersQuery>) => {
      state.userQuery = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAdminsList.fulfilled, (state: UsersListState, action) => {
        state.admins = action.payload;
        state.isLoading = false;
      })
      .addCase(getUsersList.fulfilled, (state: UsersListState, action) => {
        state.users = action.payload;
        state.isLoading = false;
      })
      .addCase(getAdminDetails.pending, (state: UsersListState) => {
        // Drop the previously viewed admin so the edit drawer never renders stale details
        // (adminDetails is not refreshed on save and survives re-login within the SPA session).
        state.adminDetails = null;
      })
      .addCase(getAdminDetails.fulfilled, (state: UsersListState, action) => {
        state.adminDetails = action.payload;
        state.isLoading = false;
      })
      .addCase(deleteAdmin.fulfilled, (state: UsersListState) => {
        state.isLoading = false;
      })
      .addMatcher(
        isPending(
          getAdminsList,
          getUsersList,
          getAdminDetails,
          updateAdminDetails,
          createAdmin,
          deleteAdmin,
          toggleActiveUser,
        ),
        (state: UsersListState) => {
          state.isLoading = true;
          state.error = null;
        },
      )
      .addMatcher(
        isRejected(
          getAdminsList,
          getUsersList,
          getAdminDetails,
          updateAdminDetails,
          createAdmin,
          deleteAdmin,
          toggleActiveUser,
        ),
        (state: UsersListState, action) => {
          const { error } = action;
          state.isLoading = false;
          state.error = error;

          showApiErrors(error);
        },
      );
  },
});

export const { resetUsersListState, changeQuery, changeUserQuery } = slice.actions;

export default slice.reducer;
